/* eslint-disable */
import _m0 from "protobufjs/minimal";

export const protobufPackage = "flight_deck";

/** Return-to-home settings for the vehicle. */
export interface ReturnSettings {
  /**
   * If true, the drone will look toward the return point while returning.
   * Otherwise, will look away. Looking away is default behavior for connectivity reasons.
   */
  faceForward: boolean;
  /**
   * Minimum height that the drone will return at. If the drone is above this height at the start
   * of return, it will not descend to this height.
   */
  minimumHeight: number;
  /** The height above the ground that the global pathfinder will return at. */
  globalPathfinderReturnHeightAgl: number;
  /** Amount of time after lost connection after which the vehicle will initiate return. */
  waitTime: number;
  /** Speed for return in m/s. Ranges from 1 m/s to maximum vehichle ground speed */
  speed: number;
  /**
   * If true, the vehicle will ascend the minimum_height above its current height when return
   * starts. Else, the vehicle will ascend to minimum_height above the takeoff point.
   * Note: Regardless of the value of ascend_from_current_height, the vehicle will always ascend
   * to at least the minimum_height above the return target height.
   * If the vehicle is already higher than the minimum_height and ascend_from_current_height is
   * false, it will not descend and maintain its current altitude during the return.
   */
  ascendFromCurrentHeight: boolean;
  /**
   * If true the vehicle will land after having returned to the lost connection destination and
   * waiting for the amount of time specified in wait_time_before_land
   */
  landOnceReturned: boolean;
  /**
   * The amount of time in seconds to wait before landing after arriving at lost connection
   * destination. If connection is regained, or the vehicle departs from the lost connection
   * destination (for whatever reason) before the time is up, we will not land
   */
  waitTimeBeforeLand: number;
  /**
   * If true, drone will not return on lost connection.
   * It will hover indefinitely (until low battery land)
   */
  dontReturnOnLostComms: boolean;
  /** Allow graph backtracking in return */
  useBacktrack: boolean;
  /** Use global pathfinder in return, this takes priority over use_backtrack */
  useGlobalPathfinder: boolean;
  /**
   * If true, the drone will automatically return upon reaching a low battery level. This is called
   * "rth" instead of "rtx" because it originally only returned to the home point, but now we allow
   * auto returning to a "critical return destination" which may be one several different points.
   * Naming kept for backwards compatibility (used in the cloud).
   * NOTE(jakub.slama): deprecated, kept for backwards compatibility only
   */
  lowBatteryAutoRth: boolean;
  /** If true, the drone will not descend after returning in any scenario */
  dontDescend: boolean;
  /**
   * If true, drone will immediately land in place if it loses
   * connection while in attitude mode.
   */
  dontReturnOnLostCommsInAtti: boolean;
  /** Above Ground Level return altitude when in Atti */
  attiReturnAltAgl: number;
  /**
   * If true, the drone will cancel the attitude return after cancel_atti_return_wait_time
   * amount of time
   */
  cancelAttiReturnWithTimer: boolean;
  /** Amount of time in seconds to wait before cancelling the attitude return */
  cancelAttiReturnWaitTime: number;
}

function createBaseReturnSettings(): ReturnSettings {
  return {
    faceForward: false,
    minimumHeight: 0,
    globalPathfinderReturnHeightAgl: 0,
    waitTime: 0,
    speed: 0,
    ascendFromCurrentHeight: false,
    landOnceReturned: false,
    waitTimeBeforeLand: 0,
    dontReturnOnLostComms: false,
    useBacktrack: false,
    useGlobalPathfinder: false,
    lowBatteryAutoRth: false,
    dontDescend: false,
    dontReturnOnLostCommsInAtti: false,
    attiReturnAltAgl: 0,
    cancelAttiReturnWithTimer: false,
    cancelAttiReturnWaitTime: 0,
  };
}

export const ReturnSettings = {
  encode(message: ReturnSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.faceForward === true) {
      writer.uint32(8).bool(message.faceForward);
    }
    if (message.minimumHeight !== 0) {
      writer.uint32(21).float(message.minimumHeight);
    }
    if (message.globalPathfinderReturnHeightAgl !== 0) {
      writer.uint32(141).float(message.globalPathfinderReturnHeightAgl);
    }
    if (message.waitTime !== 0) {
      writer.uint32(29).float(message.waitTime);
    }
    if (message.speed !== 0) {
      writer.uint32(37).float(message.speed);
    }
    if (message.ascendFromCurrentHeight === true) {
      writer.uint32(40).bool(message.ascendFromCurrentHeight);
    }
    if (message.landOnceReturned === true) {
      writer.uint32(48).bool(message.landOnceReturned);
    }
    if (message.waitTimeBeforeLand !== 0) {
      writer.uint32(61).float(message.waitTimeBeforeLand);
    }
    if (message.dontReturnOnLostComms === true) {
      writer.uint32(64).bool(message.dontReturnOnLostComms);
    }
    if (message.useBacktrack === true) {
      writer.uint32(72).bool(message.useBacktrack);
    }
    if (message.useGlobalPathfinder === true) {
      writer.uint32(128).bool(message.useGlobalPathfinder);
    }
    if (message.lowBatteryAutoRth === true) {
      writer.uint32(80).bool(message.lowBatteryAutoRth);
    }
    if (message.dontDescend === true) {
      writer.uint32(88).bool(message.dontDescend);
    }
    if (message.dontReturnOnLostCommsInAtti === true) {
      writer.uint32(96).bool(message.dontReturnOnLostCommsInAtti);
    }
    if (message.attiReturnAltAgl !== 0) {
      writer.uint32(104).int32(message.attiReturnAltAgl);
    }
    if (message.cancelAttiReturnWithTimer === true) {
      writer.uint32(112).bool(message.cancelAttiReturnWithTimer);
    }
    if (message.cancelAttiReturnWaitTime !== 0) {
      writer.uint32(125).float(message.cancelAttiReturnWaitTime);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ReturnSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseReturnSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.faceForward = reader.bool();
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.minimumHeight = reader.float();
          continue;
        case 17:
          if (tag !== 141) {
            break;
          }

          message.globalPathfinderReturnHeightAgl = reader.float();
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.waitTime = reader.float();
          continue;
        case 4:
          if (tag !== 37) {
            break;
          }

          message.speed = reader.float();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.ascendFromCurrentHeight = reader.bool();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.landOnceReturned = reader.bool();
          continue;
        case 7:
          if (tag !== 61) {
            break;
          }

          message.waitTimeBeforeLand = reader.float();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.dontReturnOnLostComms = reader.bool();
          continue;
        case 9:
          if (tag !== 72) {
            break;
          }

          message.useBacktrack = reader.bool();
          continue;
        case 16:
          if (tag !== 128) {
            break;
          }

          message.useGlobalPathfinder = reader.bool();
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.lowBatteryAutoRth = reader.bool();
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.dontDescend = reader.bool();
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.dontReturnOnLostCommsInAtti = reader.bool();
          continue;
        case 13:
          if (tag !== 104) {
            break;
          }

          message.attiReturnAltAgl = reader.int32();
          continue;
        case 14:
          if (tag !== 112) {
            break;
          }

          message.cancelAttiReturnWithTimer = reader.bool();
          continue;
        case 15:
          if (tag !== 125) {
            break;
          }

          message.cancelAttiReturnWaitTime = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ReturnSettings {
    return {
      faceForward: isSet(object.faceForward) ? globalThis.Boolean(object.faceForward) : false,
      minimumHeight: isSet(object.minimumHeight) ? globalThis.Number(object.minimumHeight) : 0,
      globalPathfinderReturnHeightAgl: isSet(object.globalPathfinderReturnHeightAgl)
        ? globalThis.Number(object.globalPathfinderReturnHeightAgl)
        : 0,
      waitTime: isSet(object.waitTime) ? globalThis.Number(object.waitTime) : 0,
      speed: isSet(object.speed) ? globalThis.Number(object.speed) : 0,
      ascendFromCurrentHeight: isSet(object.ascendFromCurrentHeight)
        ? globalThis.Boolean(object.ascendFromCurrentHeight)
        : false,
      landOnceReturned: isSet(object.landOnceReturned) ? globalThis.Boolean(object.landOnceReturned) : false,
      waitTimeBeforeLand: isSet(object.waitTimeBeforeLand) ? globalThis.Number(object.waitTimeBeforeLand) : 0,
      dontReturnOnLostComms: isSet(object.dontReturnOnLostComms)
        ? globalThis.Boolean(object.dontReturnOnLostComms)
        : false,
      useBacktrack: isSet(object.useBacktrack) ? globalThis.Boolean(object.useBacktrack) : false,
      useGlobalPathfinder: isSet(object.useGlobalPathfinder) ? globalThis.Boolean(object.useGlobalPathfinder) : false,
      lowBatteryAutoRth: isSet(object.lowBatteryAutoRth) ? globalThis.Boolean(object.lowBatteryAutoRth) : false,
      dontDescend: isSet(object.dontDescend) ? globalThis.Boolean(object.dontDescend) : false,
      dontReturnOnLostCommsInAtti: isSet(object.dontReturnOnLostCommsInAtti)
        ? globalThis.Boolean(object.dontReturnOnLostCommsInAtti)
        : false,
      attiReturnAltAgl: isSet(object.attiReturnAltAgl) ? globalThis.Number(object.attiReturnAltAgl) : 0,
      cancelAttiReturnWithTimer: isSet(object.cancelAttiReturnWithTimer)
        ? globalThis.Boolean(object.cancelAttiReturnWithTimer)
        : false,
      cancelAttiReturnWaitTime: isSet(object.cancelAttiReturnWaitTime)
        ? globalThis.Number(object.cancelAttiReturnWaitTime)
        : 0,
    };
  },

  toJSON(message: ReturnSettings): unknown {
    const obj: any = {};
    if (message.faceForward === true) {
      obj.faceForward = message.faceForward;
    }
    if (message.minimumHeight !== 0) {
      obj.minimumHeight = message.minimumHeight;
    }
    if (message.globalPathfinderReturnHeightAgl !== 0) {
      obj.globalPathfinderReturnHeightAgl = message.globalPathfinderReturnHeightAgl;
    }
    if (message.waitTime !== 0) {
      obj.waitTime = message.waitTime;
    }
    if (message.speed !== 0) {
      obj.speed = message.speed;
    }
    if (message.ascendFromCurrentHeight === true) {
      obj.ascendFromCurrentHeight = message.ascendFromCurrentHeight;
    }
    if (message.landOnceReturned === true) {
      obj.landOnceReturned = message.landOnceReturned;
    }
    if (message.waitTimeBeforeLand !== 0) {
      obj.waitTimeBeforeLand = message.waitTimeBeforeLand;
    }
    if (message.dontReturnOnLostComms === true) {
      obj.dontReturnOnLostComms = message.dontReturnOnLostComms;
    }
    if (message.useBacktrack === true) {
      obj.useBacktrack = message.useBacktrack;
    }
    if (message.useGlobalPathfinder === true) {
      obj.useGlobalPathfinder = message.useGlobalPathfinder;
    }
    if (message.lowBatteryAutoRth === true) {
      obj.lowBatteryAutoRth = message.lowBatteryAutoRth;
    }
    if (message.dontDescend === true) {
      obj.dontDescend = message.dontDescend;
    }
    if (message.dontReturnOnLostCommsInAtti === true) {
      obj.dontReturnOnLostCommsInAtti = message.dontReturnOnLostCommsInAtti;
    }
    if (message.attiReturnAltAgl !== 0) {
      obj.attiReturnAltAgl = Math.round(message.attiReturnAltAgl);
    }
    if (message.cancelAttiReturnWithTimer === true) {
      obj.cancelAttiReturnWithTimer = message.cancelAttiReturnWithTimer;
    }
    if (message.cancelAttiReturnWaitTime !== 0) {
      obj.cancelAttiReturnWaitTime = message.cancelAttiReturnWaitTime;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ReturnSettings>, I>>(base?: I): ReturnSettings {
    return ReturnSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ReturnSettings>, I>>(object: I): ReturnSettings {
    const message = createBaseReturnSettings();
    message.faceForward = object.faceForward ?? false;
    message.minimumHeight = object.minimumHeight ?? 0;
    message.globalPathfinderReturnHeightAgl = object.globalPathfinderReturnHeightAgl ?? 0;
    message.waitTime = object.waitTime ?? 0;
    message.speed = object.speed ?? 0;
    message.ascendFromCurrentHeight = object.ascendFromCurrentHeight ?? false;
    message.landOnceReturned = object.landOnceReturned ?? false;
    message.waitTimeBeforeLand = object.waitTimeBeforeLand ?? 0;
    message.dontReturnOnLostComms = object.dontReturnOnLostComms ?? false;
    message.useBacktrack = object.useBacktrack ?? false;
    message.useGlobalPathfinder = object.useGlobalPathfinder ?? false;
    message.lowBatteryAutoRth = object.lowBatteryAutoRth ?? false;
    message.dontDescend = object.dontDescend ?? false;
    message.dontReturnOnLostCommsInAtti = object.dontReturnOnLostCommsInAtti ?? false;
    message.attiReturnAltAgl = object.attiReturnAltAgl ?? 0;
    message.cancelAttiReturnWithTimer = object.cancelAttiReturnWithTimer ?? false;
    message.cancelAttiReturnWaitTime = object.cancelAttiReturnWaitTime ?? 0;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
